// The nav/layout UI slice carved out of v3's 617-line `layoutStore` god-store
// (ORP-18606, contract 05 §5/§7). It owns ONLY the sidebar + settings-nav UI state:
// pinned rail, mobile pin, settings-nav visibility, and the route to return to when the
// settings-nav closes. Everything else the god-store held (modals, news, onboarding,
// search history, the nav-visibility engine) leaves to its owning feature — see the analysis
// doc §3.
//
// v3 persisted the WHOLE store through SecureLS with no `paths`, encrypting pricingModal,
// latestNews, chartConfigs, searchHistory, … into localStorage. v4 persists exactly one
// durable user preference: `isPinnedSide`. Everything else is ephemeral (reset on nav /
// logout). No feature dependencies — this is a pure leaf, unit-testable in isolation.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

export interface PreviousRoute {
  name: string
  params: Record<string, unknown>
  query: Record<string, unknown>
}

// contract 05 §5 — NavState
export interface NavState {
  isPinnedSide: boolean
  isPinnedMobileSide: boolean
  showSettingsNav: boolean
  previousRoute: PreviousRoute | null
}

// RETIRED (ORP-19898): `selectedSettings` / `setSettingsTab` / `defaultSettingsTab`, v3's
// main+sub settings tab state. Contract 22 **CM-22-04** — owner module 05, answered
// "Yes — the host" — ratified that the URL IS the state: `/settings/workspace/basics` names the
// pane, so there is no active tab left for the shell to hold. ORP-19896 landed the other half of
// that decision (`SettingsNavTree` reads 22's `useSettingsNav()`); this is the rest.
//
// After the module split the shell structurally COULD NOT hold it anyway: "Custom Definitions"
// sub-tabs live in module 30, and 05 must not know they exist. Zero production callers ever read
// or wrote it — the only writers were this store's own resets.

/**
 * Is this route part of the settings surface? ONE discriminator: 05's own typed route meta
 * (`AppRouteMeta.settingsNav`), declared on whichever record parents `/settings`.
 *
 * Deliberately NOT the route name. v3 could test `name.startsWith('WorkspaceSettings')`
 * (`SideNavItems.vue:353`) because settings was one route with in-page tabs; in v4 `WorkspaceSettings`
 * names only the `/settings` INDEX child and each pane is its own record, so a name check would
 * close the nav the moment the user clicks a pane — and would silently exclude every pane a
 * different module registers into the host (ORP-19896).
 */
function isSettingsRoute(route: RouteLocationNormalized | undefined): boolean {
  return route?.meta.settingsNav === true
}

export const useNavStore = defineStore(
  'nav',
  () => {
    // --- state (contract 05 §5) ---
    const isPinnedSide = ref(false)
    const isPinnedMobileSide = ref(false)
    const showSettingsNav = ref(false)
    const previousRoute = ref<PreviousRoute | null>(null)

    // --- getters (contract 05 §7) ---
    /** The rail is expanded while pinned or while the settings-nav is open. */
    const isSidebarExpanded = computed(() => isPinnedSide.value || showSettingsNav.value)
    const isSettingsOpen = computed(() => showSettingsNav.value)

    // --- actions (contract 05 §7) ---
    /** Toggle the desktop rail pin (v3 `toggleSideBarPinned`). Persisted. */
    function toggleSidebar(): void {
      isPinnedSide.value = !isPinnedSide.value
    }

    /** Toggle the mobile rail pin (v3 `toggleSideBarMobilePinned`). */
    function toggleMobileSidebar(): void {
      isPinnedMobileSide.value = !isPinnedMobileSide.value
    }

    /** Force the mobile rail closed (v3 `disableMobileSideBarPinned`). */
    function closeMobileSidebar(): void {
      isPinnedMobileSide.value = false
    }

    /**
     * Open the settings-nav (v3 `openSettingsNav`), remembering `route` as the way back.
     *
     * Two routes are NOT worth remembering: one already inside the settings surface (so pane →
     * pane never rewrites "back" to point at settings itself — v3's `WorkspaceSettings` name
     * check, generalised to the meta flag), and a NAMELESS one. The latter is `START_LOCATION`,
     * the `from` of the very first navigation: a cold deep-link straight to `/settings` has no
     * previous page, and `{ name: '' }` is not a pushable target — leaving `previousRoute` null
     * makes the back control fall back to home, exactly as v3 did (`SideNavItems.vue:385`).
     */
    function openSettingsNav(route: RouteLocationNormalized): void {
      const currentName = route.name != null ? String(route.name) : ''
      if (currentName !== '' && !isSettingsRoute(route)) {
        previousRoute.value = {
          name: currentName,
          params: { ...route.params },
          query: { ...route.query },
        }
      }
      showSettingsNav.value = true
    }

    /** Close the settings-nav (v3 `closeSettingsNav`). */
    function closeSettingsNav(): void {
      showSettingsNav.value = false
    }

    /**
     * The settings-nav's WHOLE route→state binding, registered as a router `afterEach`
     * (contract 05 §11, branch #4 of the v3 god-guard, `router/index.js:54–58`). Landing on the
     * settings surface opens the takeover and remembers where the user came from; landing
     * anywhere else closes it and forgets the way back.
     *
     * `afterEach`, not a guard and not a layout watcher: this is a route SIDE-EFFECT, never a
     * navigation decision (contract 05 §8/§11 — guards stay auth/permission/cancel only), and it
     * has to be true before `AppSidebar` first paints. `AppLayout` renders the sidebar only after
     * the boot splash clears, so a watcher there would miss a cold deep-link to `/settings`;
     * `afterEach` fires on the very first navigation regardless of boot state (ORP-19896).
     *
     * `from` is now read (v3 keyed off `to` only, because v3 recorded the return route in the
     * sidebar's click handler instead — a click handler cannot see a deep link).
     */
    function syncOnNavigation(to: RouteLocationNormalized, from?: RouteLocationNormalized): void {
      if (isSettingsRoute(to)) {
        openSettingsNav(from ?? to)
        return
      }
      if (showSettingsNav.value) {
        showSettingsNav.value = false
        previousRoute.value = null
      }
    }

    /**
     * Reset the whole slice to defaults, incl. the persisted pin (v3 `resetState`'s
     * nav fields). The shell wires this into the session-reset registry (features/auth
     * `registerSessionReset`) so logout clears the nav UI; the store itself stays a pure
     * leaf with no feature import.
     */
    function reset(): void {
      isPinnedSide.value = false
      isPinnedMobileSide.value = false
      showSettingsNav.value = false
      previousRoute.value = null
    }

    return {
      isPinnedSide,
      isPinnedMobileSide,
      showSettingsNav,
      previousRoute,
      isSidebarExpanded,
      isSettingsOpen,
      toggleSidebar,
      toggleMobileSidebar,
      closeMobileSidebar,
      openSettingsNav,
      closeSettingsNav,
      syncOnNavigation,
      reset,
    }
  },
  // Persist ONLY the durable user preference. Settings-nav state is ephemeral.
  { persist: { key: 'nav', pick: ['isPinnedSide'] } },
)

export type NavStore = ReturnType<typeof useNavStore>
